import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  AcademicCapIcon,
  FireIcon,
  TrophyIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  CircleStackIcon,
  SpeakerWaveIcon,
  StarIcon as SparklesIcon
} from '@heroicons/react/24/outline';
import AIMentorController from './AIMentorController';
import API_BASE_URL from '../Config/config';
import AchievementSystem from './AchievementSystem';
import VoiceAssistant from './NewVoiceAssistant';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';

/**
 * 🚀 Unified Bootcamp Dashboard - All 5 Flows
 *
 * Single implementation for:
 * 1. Technology Mastery
 * 2. Full Stack Bundles
 * 3. Global Certifications
 * 4. Industry Job Ready (Individual)
 * 5. Industry Job Ready (Team)
 *
 * Dashboard contains only:
 * - Current Task
 * - AI Mentor
 * - Verify My Work
 * - Feedback History
 * - Progress
 * - Roadmap
 */
const UnifiedBootcampDashboard = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get enrollmentId and userId from URL params or localStorage
  const enrollmentId = searchParams.get('enrollmentId') || localStorage.getItem('currentEnrollmentId');
  const userId = searchParams.get('userId') || localStorage.getItem('userId') || localStorage.getItem('user_id');
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [progress, setProgress] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [error, setError] = useState(null);
  const [isProvisioning, setIsProvisioning] = useState(false);

  // Voice Assistant
  const [vaInitialMessage, setVaInitialMessage] = useState("");
  const {
    isVoiceAssistantOpen,
    openVoiceAssistant,
    closeVoiceAssistant,
    handleVoiceCommand,
    handleVoiceNavigation
  } = useVoiceAssistant();

  // Helper to open Voice Assistant with subtopic explanation
  const explainWithAI = async (subtopic) => {
    try {
      setVaInitialMessage(`Generating explanation for: ${subtopic}...`);
      openVoiceAssistant();
     
      // Fetch detailed explanation for the subtopic
      const res = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId
        },
        body: JSON.stringify({
          message: `Explain the subtopic "${subtopic}" for the topic "${currentTask?.title || currentTask?.topic}" in ${enrollment?.skill_slug || 'the bootcamp'}. Provide a detailed explanation that I can read.`,
          langMode: "fixed",
          langCode: "en-US",
          userId: userId
        })
      });
     
      if (res.ok) {
        const data = await res.json();
        setVaInitialMessage(data.reply);
      } else {
        setVaInitialMessage(`Sorry, I couldn't generate an explanation for "${subtopic}" right now. Please try again.`);
      }
    } catch (err) {
      console.error("Failed to explain subtopic:", err);
      setVaInitialMessage("Error connecting to AI Assistant.");
    }
  };

  useEffect(() => {
    if (!enrollmentId || !userId) {
      // No active enrollment, redirect to bootcamp selector
      navigate('/bootcamp1');
      return;
    }
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId, userId, navigate]);

  useEffect(() => {
    let interval;
    if (isProvisioning) {
      interval = setInterval(loadDashboard, 5000); // Poll every 5s during provisioning
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProvisioning]);

  const loadDashboard = React.useCallback(async () => {
    if (!isProvisioning) setLoading(true);
    setError(null);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }

      // Use existing working endpoint - tech/user-context
      // This endpoint works for Technology, TechBunch, and Certified tracks
      // userId is passed in URL path and query params, not headers (to avoid CORS issues)
      const response = await fetch(`${API_BASE_URL}/api/bootcamp/tech/user-context/${encodeURIComponent(userId)}?userId=${encodeURIComponent(userId)}`, {
        headers
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        setError(data.error || 'Failed to load dashboard');
        return;
      }

      // Check if this track should be using this dashboard
      if (data.enrollment?.track === 'technologies') {
        navigate(`/techuserdashboard?enrollmentId=${enrollmentId}&userId=${userId}`);
        return;
      }
      if (data.enrollment?.track === 'techbunch') {
        navigate(`/techbunchuserdashboard?enrollmentId=${enrollmentId}&userId=${userId}`);
        return;
      }
      if (data.enrollment?.track === 'certified') {
        navigate(`/certifieduserdashboard?enrollmentId=${enrollmentId}&userId=${userId}`);
        return;
      }
      if (data.enrollment?.track === 'roles') {
        const path = data.enrollment.workMode === 'team' ? '/team-roles' : '/individual-roles';
        navigate(`${path}?enrollmentId=${enrollmentId}&userId=${userId}`);
        return;
      }

      // Map response to our state structure
      setEnrollment(data.enrollment);
      
      const roadmapData = data.enrollment?.ai_roadmap || { roadmap: { phases: [] } };
      setRoadmap(roadmapData);

      // Automatically select the first task if none selected
      if (!currentTask && roadmapData.roadmap.phases.length > 0) {
        const firstPhase = roadmapData.roadmap.phases[0];
        if (firstPhase.tasks && firstPhase.tasks.length > 0) {
          setCurrentTask(firstPhase.tasks[0]);
        }
      }
      
      const totalTasksCount = data.enrollment?.ai_roadmap?.roadmap?.phases?.reduce((acc, phase) => acc + (phase.tasks?.length || 0), 0) || 0;
      
      setProgress({
        completed: data.enrollment?.completed_tasks_count || 0,
        total: totalTasksCount,
        percentage: totalTasksCount > 0 ? Math.round(((data.enrollment?.completed_tasks_count || 0) / totalTasksCount) * 100) : 0,
        xp: data.enrollment?.xp || 0,
        streak: data.enrollment?.streak || 0,
        rank: data.enrollment?.rank || 'Beginner',
        badges: data.enrollment?.badges || []
      });
      setRoadmap(data.enrollment?.ai_roadmap || { roadmap: { phases: [] } });
      setCompletedTasks(data.enrollment?.completed_tasks || []);

      const status = data.enrollment?.provisioning_status;
      if (status && status !== 'READY' && status !== 'FAILED') {
        setIsProvisioning(true);
      } else {
        setIsProvisioning(false);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId, userId, isProvisioning, navigate]);

  const getProvisioningMessage = (status) => {
    switch (status) {
      case 'PENDING': return 'Initializing provisioning...';
      case 'VALIDATING_GITHUB': return 'Validating GitHub account...';
      case 'CREATING_REPOSITORY': return 'Creating your dedicated repository...';
      case 'PUSHING_TEMPLATE': return 'Pushing starter template...';
      case 'GENERATING_README': return 'Generating AI documentation...';
      case 'ADDING_COLLABORATOR': return 'Inviting you as a collaborator...';
      case 'INVITATION_SENT': return 'Waiting for you to accept the invitation...';
      case 'REGISTERING_WEBHOOK': return 'Registering automation webhooks...';
      default: return 'Provisioning in progress...';
    }
  };

  const handleTaskClick = (task) => {
    setCurrentTask(task);
  };

  const handleTaskComplete = async (feedback) => {
    // Reload dashboard to update progress
    await loadDashboard();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-bgMain">
        <div className="flex flex-col items-center gap-4">
          <ArrowPathIcon className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted">Loading your bootcamp...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-bgMain">
        <div className="bg-secondary/10 border border-red-500/30 rounded-2xl p-8 max-w-md">
          <h3 className="text-red-500 font-bold text-xl mb-2">Error</h3>
          <p className="text-muted">{error}</p>
          <button
            onClick={loadDashboard}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bgMain p-4 md:p-6">
      <div className="max-w-7xl mx-auto flex flex-col h-[calc(100vh-48px)]">
        {/* Compact Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {enrollment?.skill_slug} Bootcamp
            </h1>
            <p className="text-xs text-muted">
              {enrollment?.bootcamp_type} • {enrollment?.experience_level} • User: {userId}
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end">
              <div className="flex items-center gap-2 text-primary font-bold">
                <ChartBarIcon className="h-5 w-5" />
                <span>{progress?.percentage || 0}%</span>
              </div>
              <div className="h-1.5 w-32 bg-secondary/20 rounded-full overflow-hidden mt-1">
                <div 
                  className="h-full bg-primary transition-all duration-1000"
                  style={{ width: `${progress?.percentage || 0}%` }}
                />
              </div>
            </div>
            <div className="h-10 w-px bg-secondary/20 hidden md:block"></div>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center">
                <FireIcon className="h-5 w-5 text-orange-500" />
                <span className="text-xs font-bold text-gray-300">{progress?.streak || 0}</span>
              </div>
              <div className="flex flex-col items-center">
                <TrophyIcon className="h-5 w-5 text-yellow-500" />
                <span className="text-xs font-bold text-gray-300">{progress?.xp || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Provisioning Status Banner */}
        {isProvisioning && (
          <div className="mb-6 bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="relative">
                <CircleStackIcon className="h-8 w-8 text-primary animate-pulse" />
                <ArrowPathIcon className="h-4 w-4 text-primary absolute -bottom-1 -right-1 animate-spin" />
              </div>
              <div>
                <h3 className="text-primary font-bold text-sm">
                  {getProvisioningMessage(enrollment?.provisioning_status)}
                </h3>
                <p className="text-xs text-muted">
                  We're setting up your professional environment. You can explore the roadmap while you wait.
                </p>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="h-1.5 w-48 bg-secondary/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-1000" 
                  style={{ width: enrollment?.provisioning_status === 'INVITATION_SENT' ? '90%' : '40%' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
          
          {/* Left: AI Mentor (Everything in one conversation) */}
          <div className="lg:col-span-3 flex flex-col gap-6 overflow-hidden h-full">
            {currentTask ? (
              <AIMentorController
                task={currentTask}
                enrollmentId={enrollmentId}
                userId={userId}
                experienceLevel={enrollment?.experience_level}
                onComplete={handleTaskComplete}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center bg-secondary/5 rounded-2xl border border-secondary/20 p-8 text-center">
                <AcademicCapIcon className="h-16 w-16 text-muted mb-4" />
                <h3 className="text-xl font-bold text-gray-200 mb-2">Ready to Start?</h3>
                <p className="text-muted max-w-sm">
                  Select a task from the roadmap to begin your AI-guided learning journey.
                </p>
              </div>
            )}
          </div>

          {/* Right: Progress & Roadmap Sidebar */}
          <div className="lg:col-span-1 flex flex-col gap-6 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-primary/20">
            {/* Progress Card */}
            <div className="bg-secondary/10 rounded-2xl border border-secondary/20 p-5">
              <h4 className="text-sm font-bold text-primary mb-4 flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5" />
                Your Progress
              </h4>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-muted">Total Mastery</span>
                    <span className="text-primary font-bold">{progress?.percentage}%</span>
                  </div>
                  <div className="h-2 w-full bg-secondary/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-1000"
                      style={{ width: `${progress?.percentage}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-bgMain rounded-xl p-3 border border-secondary/20">
                    <div className="flex items-center gap-1.5 text-orange-500 mb-1">
                      <FireIcon className="h-4 w-4" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">XP</span>
                    </div>
                    <div className="text-lg font-bold text-gray-200">{progress?.xp}</div>
                  </div>
                  <div className="bg-bgMain rounded-xl p-3 border border-secondary/20">
                    <div className="flex items-center gap-1.5 text-yellow-500 mb-1">
                      <TrophyIcon className="h-4 w-4" />
                      <span className="text-[10px] font-bold uppercase tracking-wider">Tasks</span>
                    </div>
                    <div className="text-lg font-bold text-gray-200">{progress?.completed}/{progress?.total}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Roadmap Card */}
            <div className="bg-secondary/10 rounded-2xl border border-secondary/20 p-5 flex-1 flex flex-col overflow-hidden">
              <h4 className="text-sm font-bold text-primary mb-4 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5" />
                Bootcamp Roadmap
              </h4>
              
              <div className="flex-1 overflow-y-auto space-y-2 pr-2 scrollbar-none">
                {roadmap?.roadmap?.phases?.map((phase, pIdx) => (
                  <div key={pIdx} className="space-y-2">
                    <div className="text-[10px] font-bold text-muted uppercase tracking-widest mt-4 mb-2 px-1">
                      Phase {pIdx + 1}: {phase.name || phase.phase_name}
                    </div>
                    {(phase.tasks || []).map((task, tIdx) => {
                      const isCompleted = completedTasks.includes(task.id || task.task_id);
                      const isCurrent = currentTask?.id === task.id || currentTask?.task_id === task.task_id;
                      
                      return (
                        <button
                          key={tIdx}
                          onClick={() => handleTaskClick(task)}
                          className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                            isCurrent 
                              ? 'bg-primary/10 border-primary/50 shadow-lg shadow-primary/5' 
                              : isCompleted
                                ? 'bg-green-500/5 border-green-500/20 hover:border-green-500/40'
                                : 'bg-secondary/5 border-secondary/20 hover:border-secondary/40'
                          }`}
                        >
                          <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                            isCompleted ? 'bg-green-500/20 text-green-500' : 'bg-secondary/20 text-muted'
                          }`}>
                            {isCompleted ? (
                              <CheckCircleIcon className="h-5 w-5" />
                            ) : (
                              <span className="text-xs font-bold">{tIdx + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-bold truncate ${isCurrent ? 'text-primary' : 'text-gray-300'}`}>
                              {task.title}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] text-muted flex items-center gap-1">
                                <ClockIcon className="h-2.5 w-2.5" />
                                {task.estimated_time || '45m'}
                              </span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                                task.difficulty === 'Expert' ? 'bg-red-500/10 text-red-500' :
                                task.difficulty === 'Intermediate' ? 'bg-yellow-500/10 text-yellow-500' :
                                'bg-green-500/10 text-green-500'
                              }`}>
                                {task.difficulty}
                              </span>
                            </div>
                            {/* Subtopics for Current Task */}
                            {isCurrent && task.subtopics?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-primary/10 flex flex-wrap gap-1.5">
                                {task.subtopics.map((sub, sIdx) => (
                                  <button
                                    key={sIdx}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      explainWithAI(sub);
                                    }}
                                    className="px-2 py-0.5 bg-primary/5 hover:bg-primary/20 border border-primary/20 rounded text-[8px] text-primary font-medium flex items-center gap-1 transition-all"
                                  >
                                    {sub}
                                    <SparklesIcon className="h-2 w-2 opacity-50" />
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <VoiceAssistant
        isOpen={isVoiceAssistantOpen}
        onClose={closeVoiceAssistant}
        initialMessage={vaInitialMessage}
        onCommand={handleVoiceCommand}
        onNavigate={handleVoiceNavigation}
        currentTask={currentTask}
        userContext={{
          userId,
          enrollmentId,
          track: enrollment?.track,
          skill: enrollment?.skill_slug
        }}
      />
      <AchievementSystem userId={userId} userProgress={enrollment?.progress} dashboardType="unified" />
    </div>
  );
};

export default UnifiedBootcampDashboard;
